/**
 * LiveKit Service — uses LivekitRepository + swagger/livekit-swagger.yaml contract.
 *
 * @typedef {Object} ConnectionDetails
 * @property {string} serverUrl
 * @property {string} roomName
 * @property {string} participantName
 * @property {string} participantToken
 *
 * @typedef {Object} GetConnectionDetailsOptions
 * @property {string} [agentName]
 * @property {string} [participantName]
 * @property {string} [participantIdentity]
 * @property {string} [roomName]
 */

import { repositories } from '../api/createRepositories';

const livekitRepository = repositories.livekit;

const LIVEKIT_URL_FALLBACK = process.env.REACT_APP_LIVEKIT_URL;

function normalizeOptions(agentNameOrOptions) {
  if (typeof agentNameOrOptions === 'string' || agentNameOrOptions === undefined) {
    return { agentName: agentNameOrOptions };
  }
  return agentNameOrOptions || {};
}

/**
 * Build request body: swagger documents room_config.agents[].agent_name;
 * optional participant fields supported by typical token backends.
 */
function buildTokenRequestBody({
  agentName,
  participantName,
  participantIdentity,
  roomName,
}) {
  const body = {
    participantName,
    participantIdentity,
    roomName,
  };
  if (agentName) {
    body.room_config = {
      agents: [{ agent_name: agentName }],
    };
  }
  return body;
}

export const livekitService = {
  /**
   * @param {string | GetConnectionDetailsOptions} [agentNameOrOptions]
   * @returns {Promise<ConnectionDetails>}
   */
  getConnectionDetails: async (agentNameOrOptions) => {
    const {
      agentName,
      participantName = 'user',
      participantIdentity: identityOverride,
      roomName: roomNameOverride,
    } = normalizeOptions(agentNameOrOptions);

    const participantIdentity =
      identityOverride ?? `voice_assistant_user_${Math.floor(Math.random() * 10_000)}`;
    const roomName =
      roomNameOverride ?? `voice_assistant_room_${Math.floor(Math.random() * 10_000)}`;

    const body = buildTokenRequestBody({
      agentName,
      participantName,
      participantIdentity,
      roomName,
    });

    const data = await livekitRepository.postConnectionToken(body);

    const participantToken = data.participantToken || data.token;
    const serverUrl = data.serverUrl || LIVEKIT_URL_FALLBACK;

    if (!serverUrl) {
      throw new Error(
        'LiveKit server URL is missing: API did not return serverUrl and REACT_APP_LIVEKIT_URL is not set'
      );
    }
    if (!participantToken) {
      throw new Error('Failed to get LiveKit token: missing participantToken in API response');
    }

    return {
      serverUrl,
      roomName: data.roomName ?? roomName,
      participantName: data.participantName ?? participantName,
      participantToken,
    };
  },
};

/**
 * Convenience export for hooks (same as livekitService.getConnectionDetails).
 * @param {string | GetConnectionDetailsOptions} [agentNameOrOptions]
 * @returns {Promise<ConnectionDetails>}
 */
export function getConnectionDetails(agentNameOrOptions) {
  return livekitService.getConnectionDetails(agentNameOrOptions);
}

export default livekitService;
